import type { BloomThemeScore } from "../types/bloom-content";
import { getGardenAssetUrl } from "./garden-assets";

export { getGardenAssetUrl };

export const THEME_ASSET_PREFIX: Record<string, string> = {
  acceptatie: "klaprozen",
  defusie: "paardenbloem",
  zelfAlsContext: "struik",
  hierEnNu: "lavendel",
  waarden: "waardenboom",
  waardengerichtHandelen: "stappenpad",
};

const GARDEN_OBJECT_LABEL: Record<string, string> = {
  klaprozen: "Klaproos",
  paardenbloem: "Paardenbloem",
  struik: "Struik",
  lavendel: "Lavendel",
  waardenboom: "Boom",
  stappenpad: "Stappenpad",
};

/** Tuin-metafoor in de modal, bijv. "Boom van waarden" i.p.v. "Waarden". */
export function gardenModalTitle(themeId: string, themeLabel: string): string {
  const prefix = THEME_ASSET_PREFIX[themeId];
  const object = prefix ? GARDEN_OBJECT_LABEL[prefix] : undefined;
  if (!object) return themeLabel;
  return `${object} van ${themeLabel.charAt(0).toLowerCase()}${themeLabel.slice(1)}`;
}

export interface ThemeGardenSlot {
  themeId: string;
  left: number;
  bottom: number;
  zIndex: number;
  maxHeightPercent: number;
}

/** Organic scene layout — boom/vijver achter, pad/klaprozen voor. */
export const THEME_GARDEN_SLOTS: ThemeGardenSlot[] = [
  { themeId: "waarden", left: 30, bottom: 32, zIndex: 2, maxHeightPercent: 85 },
  { themeId: "zelfAlsContext", left: 83, bottom: 51, zIndex: 3, maxHeightPercent: 49 },
  { themeId: "acceptatie", left: 16, bottom: 21, zIndex: 8, maxHeightPercent: 44 },
  { themeId: "defusie", left: 36, bottom: 13, zIndex: 2, maxHeightPercent: 50 },
  { themeId: "hierEnNu", left: 78, bottom: 14, zIndex: 7, maxHeightPercent: 58 },
  { themeId: "waardengerichtHandelen", left: 49, bottom: 15, zIndex: 0, maxHeightPercent: 82 },
];

export const GARDEN_TIER_COUNT = 3;

/** Niveau 1–3; grenzen op 1/3 en 2/3 van maxScore. Score 0 → niveau 1.
 *  ponytail: floor+1 i.p.v. ceil, zodat 8 (1/3 van 24) al in niveau 2 valt
 *  en 16 (2/3) al in niveau 3 — gelijk aan de labels in scoring.ts. */
export function scoreToTier(score: number, maxScore: number): number {
  if (score <= 0) return 1;
  return Math.min(3, Math.max(1, Math.floor((score / maxScore) * 3) + 1));
}

export function tierForThemeScore(score: BloomThemeScore): number {
  return scoreToTier(score.score, score.maxScore);
}

if (import.meta.env.DEV) {
  if (scoreToTier(0, 24) !== 1) throw new Error("scoreToTier(0) should be 1");
  if (scoreToTier(24, 24) !== 3) throw new Error("scoreToTier(24) should be 3");
  if (scoreToTier(7, 24) !== 1) throw new Error("scoreToTier(7) should be 1");
  if (scoreToTier(8, 24) !== 2) throw new Error("scoreToTier(8) should be 2");
  if (scoreToTier(15, 24) !== 2) throw new Error("scoreToTier(15) should be 2");
  if (scoreToTier(16, 24) !== 3) throw new Error("scoreToTier(16) should be 3");
  for (let s = 1; s <= 24; s++) {
    if (scoreToTier(s, 24) < scoreToTier(s - 1, 24)) {
      throw new Error(`scoreToTier not monotonic at ${s}`);
    }
  }
}
